import React, { useState, useEffect } from 'react';
import { Search, Target, Globe, Plus, X as XIcon, Users, Package, FileText, CircleDollarSign,
  CreditCard
 } from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { SEOSection } from '../settings/shared/SEOSection';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { IntegrationsSection } from '../settings/shared/IntegrationsSection';
import { AdvancedSection } from '../settings/shared/AdvancedSection';
import { Toggle } from '../settings/shared/FormComponents';
import { websearchService } from '../../services/websearchService';
import { firestoreService } from '../../services/firestoreService';
import { PaymentsSection } from '../settings/shared/PaymentsSection';
import { PricingSection } from '../settings/shared/PricingSection';

const DEFAULT_PRICING_SETTINGS = {
  enabled: false,
  plans: [],
  currency: 'USD',
  allowPlanSwitching: true,
  prorateUpgrades: true,
  cancelAnytime: true,
  trialEnabled: false,
  trialDays: 14,
  gracePeriodDays: 3,
  showComparison: true
};

const DEFAULT_PLAN_STRUCTURE = {
  id: '',
  name: '',
  description: '',
  price: 0,
  billingCycle: 'monthly',
  icon: 'sparkles',
  featured: false,
  popular: false,
  features: [],
  limits: {
    maxUsers: null,
    maxStorage: null,
    maxProjects: null
  },
  customFields: {},
  active: true
};

const DEFAULT_FEATURE_STRUCTURE = {
  id: '',
  text: '',
  included: true
};


export const WebSearchSettings = ({ book, bookId, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [uploadStates, setUploadStates] = useState({});
  const [errors, setErrors] = useState({});
  const [faviconFile, setFaviconFile] = useState(null);
  const [ogImageFile, setOgImageFile] = useState(null);

  const [logoFile, setLogoFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');
  const [ogImagePreview, setOgImagePreview] = useState('');
  const [headerBackgroundPreview, setHeaderBackgroundPreview] = useState('');

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: 'FileText' },
    { id: 'search', label: 'Search Config', icon: 'Search' },
    { id: 'design', label: 'Brand Design', icon: 'Palette' },
    { id: 'seo', label: 'SEO', icon: 'TrendingUp' },
    { id: 'privacy', label: 'Privacy', icon: 'Lock' },
    { id: 'pricing', label: 'Pricing', icon: 'CreditCard' },
    { id: 'payments', label: 'Payments', icon: 'CircleDollarSign' },
    { id: 'integrations', label: 'Integrations', icon: 'Plug' },
    { id: 'advanced', label: 'Advanced', icon: 'Settings' }
  ];

  useEffect(() => {
    initializeFormData();
  }, [book]);

  const initializeFormData = () => {
  
    const bookData = book || {};
    
    // Initialize pricing settings with proper defaults
    const existingPlans = bookData.pricingSettings?.plans || [];
    const normalizedPlans = existingPlans.map(plan => ({
      ...DEFAULT_PLAN_STRUCTURE,
      ...plan,
      features: (plan.features || []).map(feature => ({
        ...DEFAULT_FEATURE_STRUCTURE,
        ...feature
      })),
      limits: {
        ...DEFAULT_PLAN_STRUCTURE.limits,
        ...(plan.limits || {})
      }
    }));

    setFormData({
      // Basic Info
      botName: book?.botName || '',
      description: book?.description || '',
      logoUrl: book?.logoUrl || '',
      coverImageUrl: book?.coverImageUrl || '',

      
      headerSettings: {
        showLogo: book?.headerSettings?.showLogo ?? true,
        showTitle: book?.headerSettings?.showTitle ?? true,
        showDescription: book?.headerSettings?.showDescription ?? false,
        headerHeight: book?.headerSettings?.headerHeight ?? 80,
        logoHeight: book?.headerSettings?.logoHeight ?? 40,
        position: book?.headerSettings?.position || 'sticky',
        layout: book?.headerSettings?.layout || 'default',
        ...book?.headerSettings
      },
      
      // WebSearch Specific Settings
      websearchSettings: {
        defaultCategory: book?.websearchSettings?.defaultCategory || 'lead',
        enabledCategories: book?.websearchSettings?.enabledCategories || ['lead', 'supplier', 'tender'],
        resultsPerSearch: book?.websearchSettings?.resultsPerSearch || 10,
        groundingUrls: book?.websearchSettings?.groundingUrls || [],
        autoSaveResults: book?.websearchSettings?.autoSaveResults || false,
        enableBulkImport: book?.websearchSettings?.enableBulkImport || true,
        enableExport: book?.websearchSettings?.enableExport || true,
        enableEngagementTracking: book?.websearchSettings?.enableEngagementTracking || true,
        headerTitle: book?.websearchSettings?.headerTitle || 'Lead Scout',
        headerSubtitle: book?.websearchSettings?.headerSubtitle || 'Find Your Next Opportunity',
        defaultLocation: {
          country: book?.websearchSettings?.defaultLocation?.country || '',
          region: book?.websearchSettings?.defaultLocation?.region || '',
          city: book?.websearchSettings?.defaultLocation?.city || ''
        },
        searchPrompts: {
          lead: book?.websearchSettings?.searchPrompts?.lead || `Find 5-10 high-quality professional leads for: "{query}".
      Return ONLY valid JSON array of objects with: name, role, company, email, phone, linkedin, location, description.
      Verify all results are relevant. Output format: [{"name":"...","role":"...","company":"...","email":"..."}]`,
          supplier: book?.websearchSettings?.searchPrompts?.supplier || `Find 5-10 wholesale suppliers/manufacturers for: "{query}".
      Return ONLY valid JSON array of objects with: name, description, location, contact, email, phone, website.
      Verify all are legitimate B2B suppliers. Output format: [{"name":"...","description":"..."}]`,
          tender: book?.websearchSettings?.searchPrompts?.tender || `Find 5-10 active tenders/RFPs for: "{query}".
      Return ONLY valid JSON array of objects with: title, organization, location, deadline, value, status, description, link.
      Verify all are currently active. Output format: [{"title":"...","organization":"..."}]`
        }
      },

      pricingSettings: {
        ...DEFAULT_PRICING_SETTINGS,
        ...book.pricingSettings,
        currency: book.pricingSettings?.currency || 
                  book.paymentSettings?.currency || 
                  'USD',
        plans: normalizedPlans,
        // Ensure all boolean fields have proper values
        enabled: book.pricingSettings?.enabled === true,
        allowPlanSwitching: book.pricingSettings?.allowPlanSwitching !== false,
        prorateUpgrades: book.pricingSettings?.prorateUpgrades !== false,
        cancelAnytime: book.pricingSettings?.cancelAnytime !== false,
        trialEnabled: book.pricingSettings?.trialEnabled === true,
        showComparison: book.pricingSettings?.showComparison !== false,
        // Ensure numeric fields have proper values
        trialDays: book.pricingSettings?.trialDays || 14,
        gracePeriodDays: book.pricingSettings?.gracePeriodDays || 3
      },

      // Design
      
      primaryColor: book?.styling?.primaryColor || '#4f46e5',
      backgroundColor: book?.styling?.backgroundColor || '#0f172a',
      headerColor: book?.styling?.headerColor || '#1e293b',
      accentColor: book?.styling?.accentColor || '#8b5cf6',
      fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif',
      headerBackgroundColor: book?.styling?.headerBackgroundColor || '#ffffff',
      textColor: book?.styling?.textColor || '#1e293b',
      headerRadius: book?.styling?.headerRadius || '0px',
      logoInvert: book.styling?.logoInvert || 0,
      cardRadius: book?.styling?.cardRadius || '12px',
      shadowLevel: book?.styling?.shadowLevel || 'medium',
      // SEO
      seoTitle: book?.seoTitle || '',
      seoDescription: book?.seoDescription || '',
      seoKeywords: book?.seoKeywords || [],
      ogImage: book?.ogImage || '',
      customDomain: book?.customDomain || '',
      customPath: book?.customPath || '',
      favicon: book?.favicon || '',

      // Privacy
      accessLevel: book?.accessLevel || 'private',
      published: book?.published || false,
      authorizedUsers: book?.authorizedUsers || {},

      // Integrations
      integrations: book?.integrations || {},

      // Advanced
      customDomain: book?.customDomain || '',
      customCSS: book?.customCSS || ''
    });

    setLogoPreview(book?.logoUrl || '');
    setCoverImagePreview(book?.coverImageUrl || '');
    setHeaderBackgroundPreview(book?.headerBackgroundUrl || '');
    setFaviconPreview(book?.favicon || '');
    setOgImagePreview(book?.ogImage || '');
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          current = current[key];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      }
      
      return { ...prev, [path]: value };
    });
    setHasChanges(true);
  };

  const handleFileUpload = async (file, fileType) => {
      if (!file || !book?.id) return;
  
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, [fileType]: 'File size must be less than 5MB' }));
        return;
      }
  
      setUploadStates(prev => ({ ...prev, [fileType]: 'uploading' }));
      setErrors(prev => ({ ...prev, [fileType]: null }));
  
      try {
        const path = `books/${book.id}/${fileType}`;
        const result = await firestoreService.uploadFile(file, path, book.id);
        
        // Ensure we only store the URL string, not an object
        const urlString = typeof result === 'string' ? result : result.url;
        
        // Update formData with URL string
        const fieldName = fileType === 'logo' ? 'logoUrl' : 
                          fileType === 'coverImage' ? 'coverImageUrl' : 
                          fileType;
        handleInputChange(fieldName, urlString);
        
        // Update preview state
        if (fileType === 'logo') setLogoPreview(urlString);
        else if (fileType === 'coverImage') setCoverImagePreview(urlString);
        else if (fileType === 'headerBackground') setHeaderBackgroundPreview(urlString);
        else if (fileType === 'favicon') setFaviconPreview(urlString);
        else if (fileType === 'ogImage') setOgImagePreview(urlString);
        
        setUploadStates(prev => ({ ...prev, [fileType]: 'success' }));
        setHasChanges(true);
      } catch (error) {
        console.error(`Error uploading ${fileType}:`, error);
        setErrors(prev => ({ ...prev, [fileType]: `Failed to upload ${fileType}` }));
        setUploadStates(prev => ({ ...prev, [fileType]: 'error' }));
      }
    };
  
    const handleFaviconSelect = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        setFaviconFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setFaviconPreview(reader.result);
        reader.readAsDataURL(file);
        await handleFileUpload(file, 'favicon');
      }
    };
  
    const handleOgImageSelect = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        setOgImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setOgImagePreview(reader.result);
        reader.readAsDataURL(file);
        await handleFileUpload(file, 'ogImage');
      }
    };

  const addGroundingUrl = () => {
    if (newUrl.trim()) {
      const urls = [...(formData.websearchSettings?.groundingUrls || []), newUrl.trim()];
      handleInputChange('websearchSettings.groundingUrls', urls);
      setNewUrl('');
    }
  };

  const removeGroundingUrl = (index) => {
    const urls = formData.websearchSettings?.groundingUrls?.filter((_, i) => i !== index) || [];
    handleInputChange('websearchSettings.groundingUrls', urls);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(formData);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const renderSearchConfigTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="h-5 w-5" />
          Search Configuration
        </h3>

        <div className="space-y-4">
          {/* Default Category */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Default Category
            </label>
            <p className="text-sm text-gray-500">Default search category on load</p>
            <select
              value={formData.websearchSettings?.defaultCategory || 'lead'}
              onChange={(e) => handleInputChange('websearchSettings.defaultCategory', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="lead">Leads</option>
              <option value="supplier">Suppliers</option>
              <option value="tender">Tenders</option>
            </select>
          </div>

          {/* Enabled Categories */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Enabled Categories
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Allow users to search these categories
            </p>
            <div className="space-y-2">
              {['lead', 'supplier', 'tender'].map(category => (
                <label key={category} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.websearchSettings?.enabledCategories?.includes(category)}
                    onChange={(e) => {
                      const current = formData.websearchSettings?.enabledCategories || [];
                      const updated = e.target.checked
                        ? [...current, category]
                        : current.filter(c => c !== category);
                      handleInputChange('websearchSettings.enabledCategories', updated);
                    }}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    {category === 'lead' && <Users className="h-4 w-4 text-indigo-600" />}
                    {category === 'supplier' && <Package className="h-4 w-4 text-purple-600" />}
                    {category === 'tender' && <FileText className="h-4 w-4 text-blue-600" />}
                    <span className="capitalize">{category}s</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Results Per Search */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Results Per Search
            </label>
            <p className="text-sm text-gray-500">Number of results to return per search (5-20)</p>
            <input
              type="number"
              min="5"
              max="20"
              value={formData.websearchSettings?.resultsPerSearch || 10}
              onChange={(e) => handleInputChange('websearchSettings.resultsPerSearch', parseInt(e.target.value) || 10)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Header Title */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Header Title
            </label>
            <p className="text-sm text-gray-500">Main title shown to users</p>
            <input
              type="text"
              value={formData.websearchSettings?.headerTitle || ''}
              onChange={(e) => handleInputChange('websearchSettings.headerTitle', e.target.value)}
              placeholder="Lead Scout"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Header Subtitle */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Header Subtitle
            </label>
            <p className="text-sm text-gray-500">Subtitle below main title</p>
            <input
              type="text"
              value={formData.websearchSettings?.headerSubtitle || ''}
              onChange={(e) => handleInputChange('websearchSettings.headerSubtitle', e.target.value)}
              placeholder="Find Your Next Opportunity"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Default Location */}
            <div className="border-t pt-4 mt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Default Location</h4>
            <p className="text-sm text-gray-500 mb-4">
                Set a default location for searches (users can override this)
            </p>
            
            <div className="space-y-4">
                {/* Country */}
                <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    Country
                </label>
                <select
                    value={formData.websearchSettings?.defaultLocation?.country || ''}
                    onChange={(e) => handleInputChange('websearchSettings.defaultLocation.country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="">Global (No country filter)</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                    <option value="South Africa">South Africa</option>
                    <option value="India">India</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Spain">Spain</option>
                    <option value="Italy">Italy</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="Belgium">Belgium</option>
                    <option value="Switzerland">Switzerland</option>
                    <option value="Austria">Austria</option>
                    <option value="Poland">Poland</option>
                    <option value="Sweden">Sweden</option>
                    <option value="Norway">Norway</option>
                    <option value="Denmark">Denmark</option>
                    <option value="Finland">Finland</option>
                    <option value="Ireland">Ireland</option>
                    <option value="New Zealand">New Zealand</option>
                    <option value="Singapore">Singapore</option>
                    <option value="Hong Kong">Hong Kong</option>
                    <option value="Japan">Japan</option>
                    <option value="China">China</option>
                    <option value="Brazil">Brazil</option>
                    <option value="Mexico">Mexico</option>
                    <option value="Argentina">Argentina</option>
                    <option value="Chile">Chile</option>
                    <option value="Colombia">Colombia</option>
                    <option value="United Arab Emirates">United Arab Emirates</option>
                    <option value="Saudi Arabia">Saudi Arabia</option>
                    <option value="Israel">Israel</option>
                    <option value="Turkey">Turkey</option>
                    <option value="Egypt">Egypt</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Ghana">Ghana</option>
                </select>
                </div>

                {/* State/Province */}
                <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    State/Province (Optional)
                </label>
                <input
                    type="text"
                    value={formData.websearchSettings?.defaultLocation?.region || ''}
                    onChange={(e) => handleInputChange('websearchSettings.defaultLocation.region', e.target.value)}
                    placeholder="e.g., California, Ontario, KwaZulu-Natal"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                </div>

                {/* City */}
                <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    City/Town (Optional)
                </label>
                <input
                    type="text"
                    value={formData.websearchSettings?.defaultLocation?.city || ''}
                    onChange={(e) => handleInputChange('websearchSettings.defaultLocation.city', e.target.value)}
                    placeholder="e.g., New York, Toronto, Durban"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                </div>

                {/* Preview */}
                {(formData.websearchSettings?.defaultLocation?.country || 
                formData.websearchSettings?.defaultLocation?.region || 
                formData.websearchSettings?.defaultLocation?.city) && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <p className="text-sm font-medium text-indigo-900">Location Preview:</p>
                    <p className="text-sm text-indigo-700 mt-1">
                    {[
                        formData.websearchSettings?.defaultLocation?.city,
                        formData.websearchSettings?.defaultLocation?.region,
                        formData.websearchSettings?.defaultLocation?.country
                    ].filter(Boolean).join(', ') || 'Global'}
                    </p>
                </div>
                )}
            </div>
            </div>

          {/* Feature Toggles */}
          <div className="border-t pt-4 mt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Feature Toggles</h4>
            <div className="space-y-3">
              <Toggle
                label="Auto-save Results"
                description="Automatically save all search results"
                checked={formData.websearchSettings?.autoSaveResults || false}
                onChange={(checked) => handleInputChange('websearchSettings.autoSaveResults', checked)}
              />

              <Toggle
                label="Enable Bulk Import"
                description="Allow CSV imports"
                checked={formData.websearchSettings?.enableBulkImport ?? true}
                onChange={(checked) => handleInputChange('websearchSettings.enableBulkImport', checked)}
              />

              <Toggle
                label="Enable Export"
                description="Allow CSV exports"
                checked={formData.websearchSettings?.enableExport ?? true}
                onChange={(checked) => handleInputChange('websearchSettings.enableExport', checked)}
              />

              <Toggle
                label="Engagement Tracking"
                description="Track notes and engagement history"
                checked={formData.websearchSettings?.enableEngagementTracking ?? true}
                onChange={(checked) => handleInputChange('websearchSettings.enableEngagementTracking', checked)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Grounding URLs */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Globe className="h-5 w-5 text-indigo-600" />
          Grounding URLs
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Specify domains to focus search results. Leave empty to search the entire web.
        </p>

        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="example.com"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addGroundingUrl();
                }
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <button
              type="button"
              onClick={addGroundingUrl}
              disabled={!newUrl.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          {formData.websearchSettings?.groundingUrls?.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {formData.websearchSettings.groundingUrls.map((url, index) => (
                <div key={index} className="flex items-center justify-between bg-indigo-50 p-3 rounded-lg border border-indigo-100 group hover:border-indigo-200 transition-colors">
                  <span className="text-sm text-gray-700 font-medium flex items-center gap-2">
                    <Globe className="h-4 w-4 text-indigo-600" />
                    {url}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeGroundingUrl(index)}
                    className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                    title="Remove URL"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {formData.websearchSettings?.groundingUrls?.length === 0 && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <Globe className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No grounding URLs added</p>
              <p className="text-xs text-gray-400 mt-1">Searches will cover the entire web</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfoTab
            formData={formData}
            onInputChange={handleInputChange}
            onFileUpload={handleFileUpload}
            errors={errors}
            bookId={bookId}
            templateType="websearch"
          />
        );

        case 'header':
          return (
            <HeaderSettings
                formData={formData}
                onInputChange={handleInputChange}
                onImageUpload={handleFileUpload}
            />
          );

      case 'search':
        return renderSearchConfigTab();

      case 'design':
        return (
          <DesignBrandingSection
            formData={formData}
            onInputChange={handleInputChange}
          />
        );

      case 'seo':
        return (
          <SEOSection
            formData={formData}
            onInputChange={handleInputChange}
            onFileUpload={handleFileUpload}
            uploadStates={uploadStates}
            errors={errors}
            faviconPreview={formData.favicon || ''}
            ogImagePreview={formData.ogImage || ''}
            onRemoveFavicon={() => handleInputChange('favicon', '')}
            onRemoveOgImage={() => handleInputChange('ogImage', '')}
          />
         );

      case 'privacy':
        return (
          <PrivacySection
            formData={formData}
            onInputChange={handleInputChange}
          />
        );

      case 'integrations':
        return (
          <IntegrationsSection
            formData={formData}
            onInputChange={handleInputChange}
          />
        );

      case 'payments':
        return (
          <PaymentsSection
            formData={formData}
            onInputChange={handleInputChange}
          />
        );

      case 'pricing':
        return (
            <PricingSection
            formData={formData}
            onInputChange={handleInputChange}
          />
        );

      case 'advanced':
        return (
          <AdvancedSection
            formData={formData}
            onInputChange={handleInputChange}
          />
        );

      default:
        return null;
    }
  };

  return (
    <SettingsModal
      isOpen={true}
      onClose={onClose}
      title="WebSearch Settings"
      subtitle="Configure your AI-powered search template"
      onSave={handleSave}
      saving={saving}
      hasChanges={hasChanges}
      icon={Search}
    >
      <div className="flex h-full overflow-hidden">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </SettingsModal>
  );
};